import { ofType } from "redux-observable";
import { createSelector } from "reselect";
import { filter, map, switchMap } from "rxjs/operators";
import JobProApi from "../../../dependencies/JobProApi";
import moment from "moment";
import {
  updateEvent,
  scheduleAppointmentExplicit,
  scheduleIndividualAppointmentsExplicit,
  splitAppointmentExplicit,
  reloadCalendar,
  CALENDAR_FETCH,
} from "../../calendar";
import { updateJob } from "../../jobs";
import { fetchCalendar } from "../../calendar";

// Fields

const APPOINTMENT_FIELDS = "id,name";
const CUSTOMER_FIELDS = "id,name";
const CREW_FIELDS =
  "id,equipment(id,color,displayOrder,name,workCapacities(capacityType,value)),resources(resource(id,firstName,lastName),resourcePercentage)";
const JOB_FIELDS =
  "id,capacities(estimatedDuration,laborAmount,materialQuantity),contacts(isPrimaryContact,name,firstName,lastName,cellPhone,workPhone,email),customer(id,name),estimatedStartDate,items(item(itemType(id,name)),proposedQuantity),geoPoint(latitude,longitude),jobNumber,jobTotal,jobType,location,office(id,name),name,profit(actualProfit,proposedProfit),salesperson(calendarColor),status,priorityNotes";
const CALENDAR_FIELDS = `id,apptType,crew(${CREW_FIELDS}),description,displayOrder,endDate,endTime,job(${JOB_FIELDS}),startDate,startTime,customer(id,name),datasheet(id,name)`;
//const CALENDAR_FIELDS = `description,appttype`;

// Action types
const EDIT_APPOINTMENT_MODAL_UPDATE = "EDIT_APPOINTMENT_MODAL_UPDATE";
const EDIT_APPOINTMENT_MODAL_UPDATE_CONFIRM =
  "EDIT_APPOINTMENT_MODAL_UPDATE_CONFIRM";
const EDIT_APPOINTMENT_MODAL_CONFIRM = "EDIT_APPOINTMENT_MODAL_CONFIRM";
const EDIT_APPOINTMENT_MODAL_INDIVIDUAL_CONFIRM =
  "EDIT_APPOINTMENT_MODAL_INDIVIDUAL_CONFIRM";
const EDIT_APPOINTMENT_MODAL_SPLIT_CONFIRM =
  "EDIT_APPOINTMENT_MODAL_SPLIT_CONFIRM";
const FETCH_APPOINTMENT_MODAL_COMPLETE = "FETCH_APPOINTMENT_MODAL_COMPLETE";
const TOGGLE_APPOINTMENT_MODAL = "TOGGLE_APPOINTMENT_MODAL";
const TOGGLE_APPOINTMENT_CREW = "TOGGLE_APPOINTMENT_CREW";
const TOGGLE_EDIT_APPOINTMENT_MODAL = "TOGGLE_EDIT_APPOINTMENT_MODAL";

const DEFAULT_STATE = {
  equipment: [],
  eventCrews: [],
  form: {},
  isApptEditCrewEnabled: false,
  event: null,
  loading: true,
  resources: [],
  customers: [],
  datasheets: [],
  show: false,
};

// Reducer
export default function (state = DEFAULT_STATE, { payload, type }) {
  switch (type) {
    case EDIT_APPOINTMENT_MODAL_UPDATE:
      return {
        ...state,
        form: {
          ...state.form,
          ...payload,
        },
      };
    case FETCH_APPOINTMENT_MODAL_COMPLETE:
      return {
        ...state,
        ...payload,
        form: payload
          ? {
              ...payload.event,
            }
          : {},
        loading: false,
      };
    case TOGGLE_APPOINTMENT_MODAL:
      return {
        ...state,
        ...payload,
        loading: true,
      };
    case TOGGLE_EDIT_APPOINTMENT_MODAL:
      return {
        ...state,
        ...payload,
        loading: true,
      };

    case TOGGLE_APPOINTMENT_CREW:
      return {
        ...state,
        isApptEditCrewEnabled: !state.isApptEditCrewEnabled,
      };
    default:
      return state;
  }
}

// Action creators
export const editAppointmentModalUpdate = (changes) => ({
  type: EDIT_APPOINTMENT_MODAL_UPDATE,
  payload: changes,
});

export const editAppointmentModalUpdateConfirm = (changes) => ({
  type: EDIT_APPOINTMENT_MODAL_UPDATE_CONFIRM,
  payload: changes,
});

export const editAppointmentModalConfirm = () => ({
  type: EDIT_APPOINTMENT_MODAL_CONFIRM,
});

export const editAppointmentModalIndividualConfirm = () => ({
  type: EDIT_APPOINTMENT_MODAL_INDIVIDUAL_CONFIRM,
});

export const editAppointmentModalSplitConfirm = () => ({
  type: EDIT_APPOINTMENT_MODAL_SPLIT_CONFIRM,
});

export const toggleAppointmentCrew = () => ({
  type: TOGGLE_APPOINTMENT_CREW,
});

export const toggleAppointmentModal = (payload) => ({
  type: TOGGLE_APPOINTMENT_MODAL,
  payload,
});

const fetchAppointmentModalComplete = (payload) => ({
  type: FETCH_APPOINTMENT_MODAL_COMPLETE,
  payload,
});

// Epics
export const confirmAppointmentModalEpic = (action$, state$) =>
  action$.pipe(
    ofType(EDIT_APPOINTMENT_MODAL_CONFIRM),
    map(() => {
      const { form } = state$.value.modals.appintmentCalendarModal;
      const {
        crew,
        description,
        endDate,
        endTime,
        customer,
        datasheet,
        job,
        appointmentType,
        startDate,
        startTime,
      } = form;

      return async (dispatch, getState) => {
        // Update crew (if needed)
        var appttype = appointmentType ? appointmentType.name : "";

        dispatch(
          scheduleAppointmentExplicit(
            datasheet ? datasheet.id : 0,
            customer ? customer.id : 0,
            appttype,
            {
              crew: crew
                ? {
                    equipment: crew.equipment
                      ? { id: crew.equipment.id }
                      : null,
                    resources: crew.resources.map(
                      ({ resource, resourcePercentage }) => ({
                        resource: { id: resource.id },
                        resourcePercentage,
                      })
                    ),
                  }
                : null,
            },
            startDate,
            startTime,
            endDate,
            endTime,
            description,
            job ? job.id : 0
          )
        );

        // Close the modal
        dispatch(toggleAppointmentModal({ show: false }));
        const { date, mode } = getState().calendar;
        dispatch(fetchCalendar(date, mode));
      };
    })
  );

export const confirmIndividualAppointmentModalEpic = (action$, state$) =>
  action$.pipe(
    ofType(EDIT_APPOINTMENT_MODAL_INDIVIDUAL_CONFIRM),
    map(() => {
      return async (dispatch, getState) => {
        const { form } = state$.value.modals.appintmentCalendarModal;
        const {
          crew,
          description,
          endDate,
          endTime,
          customer,
          datasheet,
          job,
          appointmentType,
          startDate,
          startTime,
        } = form;

        // Check if start and end dates span at least two days
        const start = new Date(startDate);
        const end = new Date(endDate);

        if (moment(start).isSame(end)) {
          alert(
            "To split this event, the start and end dates must span at least two days."
          );
          return; // Don't proceed with the operation
        }

        // Show confirmation dialog
        const confirmed = window.confirm(
          "Are you sure you want this to be converted into individual, daily events? This cannot be undone."
        );

        if (!confirmed) {
          return; // User cancelled the operation
        }

        // Update crew (if needed)
        var appttype = appointmentType ? appointmentType.name : "";

        dispatch(
          scheduleIndividualAppointmentsExplicit(
            datasheet ? datasheet.id : 0,
            customer ? customer.id : 0,
            appttype,
            {
              crew: crew
                ? {
                    equipment: crew.equipment
                      ? { id: crew.equipment.id }
                      : null,
                    resources: crew.resources.map(
                      ({ resource, resourcePercentage }) => ({
                        resource: { id: resource.id },
                        resourcePercentage,
                      })
                    ),
                  }
                : null,
            },
            startDate,
            startTime,
            endDate,
            endTime,
            description,
            job ? job.id : 0
          )
        );

        // Close the modal
        dispatch(toggleAppointmentModal({ show: false }));
        const { date, mode } = getState().calendar;
        dispatch(fetchCalendar(date, mode));
      };
    })
  );

export const confirmSplitAppointmentModalEpic = (action$, state$) =>
  action$.pipe(
    ofType(EDIT_APPOINTMENT_MODAL_SPLIT_CONFIRM),
    map(() => {
      return async (dispatch, getState) => {
        const { form } = state$.value.modals.appintmentCalendarModal;
        const { id, startDate, endDate } = form;

        // Check if start and end dates span at least two days
        const start = new Date(startDate);
        const end = new Date(endDate);

        console.log("hit", id, startDate, endDate);

        if (moment(start).isSame(end)) {
          alert(
            "To split this event, the start and end dates must span at least two days."
          );
          return; // Don't proceed with the operation
        }

        // Show confirmation dialog
        const confirmed = window.confirm(
          "Are you sure you want this to be converted into individual, daily events? This cannot be undone."
        );

        if (!confirmed) {
          return; // User cancelled the operation
        }

        dispatch(splitAppointmentExplicit(id, startDate));

        // Close the modal
        dispatch(toggleAppointmentModal({ show: false }));
        const { date, mode } = getState().calendar;
        dispatch(fetchCalendar(date, mode));
      };
    })
  );

export const confirmEditApptModalEpic = (action$, state$) =>
  action$.pipe(
    ofType(EDIT_APPOINTMENT_MODAL_UPDATE_CONFIRM),
    map(() => {
      const { event, form } = state$.value.modals.appintmentCalendarModal;
      const {
        crew,
        description,
        endDate,
        endTime,
        customer,
        datasheet,
        appointmentType,
        startDate,
        startTime,
      } = form;
      return async (dispatch, getState) => {
        // Update crew (if needed)
        //console.log(form );

        if (state$.value.settings.schedulerView == "equipment") {
          dispatch(
            updateEvent(form.id, {
              crew: form.crew,
              description: form.description,
              endDate: form.endDate,
              endTime: form.endTime,
              startDate: form.startDate,
              startTime: form.startTime,
              appttype: form.appointmentType ? form.appointmentType.name : "",
              customer: form.customer,
              datasheet: form.datasheet,
              job: form.job,
            })
          );
        }
        dispatch(toggleAppointmentModal({ show: false }));
        const { date, mode } = getState().calendar;
        dispatch(fetchCalendar(date, mode));
      };
    })
  );

export const toggleApptEditModal = (payload) => ({
  type: TOGGLE_EDIT_APPOINTMENT_MODAL,
  payload: payload,
});

export const toggleEditAppoinmentModalEpic = (action$) =>
  action$.pipe(
    ofType(TOGGLE_EDIT_APPOINTMENT_MODAL),
    filter(({ payload }) => payload.show),
    switchMap(({ payload }) => {
      const { eventid } = payload;
      const calendarPromise = JobProApi.calendar.get(eventid, {
        fields: CALENDAR_FIELDS,
      });

      const customersPromise = JobProApi.customers.query({
        fields: "id,name",
        limit: 500,
      });

      const equipmentPromise = JobProApi.equipment.query(
        {
          schedulable: true,
          "status!": "Inactive",
        },
        {
          fields: "id,name",
          limit: 500,
        }
      );

      const resourcesPromise = JobProApi.resources.query(
        {
          isActive: true,
        },
        {
          fields: "id,calendarColor,firstName,lastName",
        }
      );

      return Promise.all([
        calendarPromise,
        equipmentPromise,
        resourcesPromise,
        customersPromise,
      ]).then(([event, equipment, resources, customers]) => ({
        event,
        equipment,
        resources,
        customers,
      }));
    }),
    map(fetchAppointmentModalComplete)
  );

export const toggleAppointmentModalEpic = (action$) =>
  action$.pipe(
    ofType(TOGGLE_APPOINTMENT_MODAL),
    switchMap(() => {
      const customersPromise = JobProApi.customers.query({
        fields: "id,name",
        limit: 999999,
      });

      const equipmentPromise = JobProApi.equipment.query(
        {
          schedulable: true,
          "status!": "Inactive",
        },
        {
          fields: "id,name",
          limit: 9999,
        }
      );

      const resourcesPromise = JobProApi.resources.query(
        {
          isActive: true,
        },
        {
          fields: "id,calendarColor,firstName,lastName",
        }
      );

      return Promise.all([
        customersPromise,
        resourcesPromise,
        equipmentPromise,
      ]).then(([customers, resources, equipment]) => ({
        customers,
        resources,
        equipment,
      }));
    }),
    map(fetchAppointmentModalComplete)
  );

// Selectors
const getAppointmentOffice = (event) => {
  if (!event) {
    return null;
  } else if (event.purchaseOrder) {
    return event.purchaseOrder.office;
  } else if (event.job) {
    return event.job.office;
  } else {
    return null;
  }
};

const getAppointmentStandardCrewFilter = (event) => {
  if (!event) {
    return (c) => true;
  } else if (event.purchaseOrder) {
    return (c) => c.deliveryCrew;
  } else if (event.job) {
    return (c) => c.installationCrew;
  } else {
    return (c) => true;
  }
};

const standardCrewsSelector = createSelector(
  (state) => state.standardCrews.crews,
  (state) => state.modals.editModal.event,
  (standardCrews, event) => {
    const office = getAppointmentOffice(event);
    return standardCrews
      .filter(getAppointmentStandardCrewFilter(event))
      .map((c) => ({
        ...c,
        disabled: !!office && !!c.office && c.office.id !== office.id,
      }));
  }
);

export const appointmentModalSelector = (state) => ({
  show: state.show,
  appointmentTypes: state.dropdown.appointmentTypes,
  ...state,
});
